import React from "react";
import { menuHighlights, menuLookupKeys, restaurantInfo, deliveryInfo } from "../data/mock";
import { useCart } from "../context/CartContext";
import { Plus, ExternalLink, Truck } from "lucide-react";

const FEATURED_IDS = [1, 2, 7]; // Attieké Feast, Fish & Plantains, Pepper Soup & Fufu

function toNumber(p) {
  return parseFloat((p || "0").replace(/[^\d.]/g, "")) || 0;
}

export default function OrderOnline() {
  const { add, setOpen } = useCart();
  const featured = menuHighlights.filter((m) => FEATURED_IDS.includes(m.id));

  return (
    <section id="order" className="py-24 md:py-36 border-t border-[var(--mm-line)]">
      <div className="max-w-[1300px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center mb-16">
          <div className="md:col-span-6 fade-in">
            <div className="section-label mb-4">Order Online</div>
            <h2 className="font-display text-5xl md:text-7xl italic font-light text-[var(--mm-cream)] leading-[0.95] mb-6">
              Order direct.<br />
              <span className="text-[var(--mm-accent)]">Pay in seconds.</span>
            </h2>
            <p className="text-[var(--mm-cream-muted)] font-light leading-relaxed text-lg max-w-md mb-8">
              Skip the middleman. Add your favourites, checkout with Stripe, and mama will have your feast ready for pickup — hot, fresh, and made with love.
            </p>
            <div className="flex flex-wrap gap-3">
              <a href="#menu" className="btn-primary">Browse Full Menu</a>
              <button onClick={() => setOpen(true)} className="btn-outline">View Cart</button>
            </div>
          </div>

          <div className="md:col-span-6 fade-in">
            <div className="grid grid-cols-1 gap-4">
              {featured.map((item) => {
                const lookup_key = menuLookupKeys[item.id];
                return (
                  <div
                    key={item.id}
                    className="flex items-center gap-5 p-4 border border-[var(--mm-line)] bg-[var(--mm-bg-alt)] hover:border-[var(--mm-accent)] transition-colors duration-500"
                  >
                    <img src={item.image} alt={item.name} className="w-20 h-20 md:w-24 md:h-24 object-cover shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-[0.66rem] tracking-[0.28em] uppercase text-[var(--mm-accent)] mb-1">{item.tag}</div>
                      <div className="font-display italic text-xl text-[var(--mm-cream)] leading-tight truncate">{item.name}</div>
                      <div className="font-display text-[var(--mm-cream-muted)] mt-1">{item.price}</div>
                    </div>
                    {lookup_key && (
                      <button
                        onClick={() =>
                          add({
                            lookup_key,
                            name: item.name,
                            price: toNumber(item.price),
                            image: item.image
                          })
                        }
                        aria-label={`Add ${item.name} to order`}
                        className="shrink-0 w-11 h-11 flex items-center justify-center border border-[var(--mm-accent)] text-[var(--mm-accent)] hover:bg-[var(--mm-accent)] hover:text-[var(--mm-bg)] transition-all duration-300"
                      >
                        <Plus size={18} />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Private Delivery banner */}
        <div className="mb-10 p-6 md:p-8 border border-[var(--mm-accent)] bg-[var(--mm-bg-alt)] flex flex-col md:flex-row items-start md:items-center gap-6 fade-in">
          <div className="w-12 h-12 shrink-0 rounded-full border border-[var(--mm-accent)] flex items-center justify-center">
            <Truck size={20} className="text-[var(--mm-accent)]" />
          </div>
          <div className="flex-1">
            <div className="flex flex-col sm:flex-row sm:items-baseline sm:gap-4 mb-2">
              <h3 className="font-display italic text-2xl text-[var(--mm-cream)]">{deliveryInfo.headline}</h3>
              <div className="text-xs tracking-[0.28em] uppercase text-[var(--mm-accent)]">
                Private delivery only · Minimum ${deliveryInfo.minOrder} · {deliveryInfo.areas}
              </div>
            </div>
            <p className="text-sm text-[var(--mm-cream-muted)] font-light leading-relaxed">
              {deliveryInfo.note}
            </p>
          </div>
          <a href={`tel:${restaurantInfo.contact.phone}`} className="btn-outline shrink-0">
            Enquire
          </a>
        </div>

        {/* Delivery apps footer strip */}
        <div className="pt-10 border-t border-[var(--mm-line)] fade-in">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <span className="text-xs tracking-[0.28em] uppercase text-[var(--mm-cream-muted)]">
              Prefer a delivery app? Also on
            </span>
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              {restaurantInfo.delivery.map((s) => (
                <a
                  key={s.name}
                  href={s.url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm text-[var(--mm-cream)] hover:text-[var(--mm-accent)] transition-colors"
                >
                  {s.name}
                  <ExternalLink size={12} />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
